import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  Inbox,
  RefreshCw,
  RotateCcw,
  Wallet,
  X,
} from "lucide-react";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import { companyOrderApiService } from "../../../../infrastructure/api/company/company-order-api-service";
import type {
  BankTransferDepositCandidate,
  BankTransferDepositSummary,
  BankTransferTransaction,
  UnpaidBankTransferOrder,
} from "../../../../domain/types/bank-transfer-auto-confirm";
import { dateTime, elapsedDays, isoDate, money } from "../bankTransferFormat";

/**
 * 주문 관리 › 무통장입금 확인.
 *
 * 주인공은 **입금을 기다리는 주문**이지 입금 목록이 아니다.
 * 개인 계좌를 연결하면 이자나 개인 송금까지 수집되는데, 그걸 "확인 필요"로 늘어놓으면
 * 아무리 처리해도 줄지 않는 할 일 목록이 된다. 입금은 주문을 열었을 때만 후보로 보여준다.
 *
 * 자동확인을 쓰지 않는 회사도 이 화면을 쓴다. 하는 일은 같고 확인 방법만 다르다 —
 * 자동확인이 있으면 후보에서 고르고, 없으면 은행 앱에서 보고 직접 결제 확인을 누른다.
 */
/** 확인된 입금 조회 범위. 주말을 넘겨 보려면 하루로는 모자란다 */
const RANGES = [
  { days: 1, label: "오늘" },
  { days: 7, label: "7일" },
  { days: 30, label: "30일" },
] as const;

export const DepositTab: React.FC<{
  companyId: number;
  /** 자동확인을 시작하러 가는 길. 이 화면에서 바로 마법사를 연다 */
  onStartAuto: () => void;
}> = ({ companyId, onStartAuto }) => {
  const [summary, setSummary] = useState<BankTransferDepositSummary>();
  const [unpaid, setUnpaid] = useState<UnpaidBankTransferOrder[]>([]);
  const [deposits, setDeposits] = useState<BankTransferTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState<UnpaidBankTransferOrder>();
  /**
   * 확인된 입금을 며칠치 볼 것인가.
   *
   * 하루만 보면 **주말에 들어온 입금을 월요일에 못 본다.** 사장님이 매일 이 화면을
   * 여는 것도 아니라서 기본을 일주일로 둔다.
   */
  const [days, setDays] = useState(7);
  const [busyOrderId, setBusyOrderId] = useState<number>();
  const [busyDepositId, setBusyDepositId] = useState<number>();
  /** 수기로 결제 확인할 주문. 고를 입금이 없으니 확인만 받는다 */
  const [confirming, setConfirming] = useState<UnpaidBankTransferOrder>();

  const autoConfirm = summary?.autoConfirmEnabled ?? false;
  const rangeLabel = days === 1 ? "오늘" : `최근 ${days}일간`;

  const reload = useCallback(async () => {
    if (!Number.isFinite(companyId)) return;
    setLoading(true);
    setError("");
    try {
      const today = isoDate(new Date());
      const since = new Date();
      // days 일치를 본다. 오늘을 포함하므로 하루를 뺀다 (7일 = 오늘 + 지난 6일)
      since.setDate(since.getDate() - (days - 1));
      const [nextSummary, nextUnpaid] = await Promise.all([
        bankTransferAutoConfirmApiService.depositSummary(companyId, today),
        bankTransferAutoConfirmApiService.unpaidOrders(companyId),
      ]);
      setSummary(nextSummary);
      setUnpaid(nextUnpaid);
      // 자동확인을 쓰지 않으면 수집된 입금이 없다. 부를 이유도 없다.
      setDeposits(
        nextSummary.autoConfirmEnabled
          ? await bankTransferAutoConfirmApiService.deposits(companyId, {
              from: isoDate(since),
              to: today,
            })
          : [],
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [companyId, days]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 자동확인 미사용. 사장님이 은행에서 본 것을 그대로 반영한다.
   *
   * **통장을 조회하지 않는다.** 누르는 순간 그 주문이 접수로 넘어갈 뿐이라,
   * 확인창에서 그 사실을 분명히 말한다.
   */
  const confirmManually = async (order: UnpaidBankTransferOrder) => {
    setConfirming(undefined);
    setBusyOrderId(order.orderId);
    setError("");
    try {
      await companyOrderApiService.confirmPayment(
        companyId,
        order.orderId,
        "BANK_TRANSFER",
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "결제 확인에 실패했습니다.");
      // 다른 창이 먼저 처리했을 수 있다. 실패한 채로 두면 없어진 주문이 계속 보인다.
      await reload().catch(() => undefined);
    } finally {
      setBusyOrderId(undefined);
    }
  };

  /** 잘못 확인한 입금 되돌리기. 사람은 실수하고 자동매칭도 동명이인이면 틀린다. */
  const unlink = async (item: BankTransferTransaction) => {
    if (
      !window.confirm(
        `주문 #${item.matchedOrderId}의 입금 확인을 취소할까요?\n` +
          `${money(item.depositAmount)} · ${item.depositorName ?? "이름 없음"}\n\n` +
          "주문은 결제전으로 돌아가고, 이 입금은 다시 고르실 수 있습니다.",
      )
    )
      return;
    setBusyDepositId(item.id);
    setError("");
    try {
      await bankTransferAutoConfirmApiService.unlink(companyId, item.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "확인 취소에 실패했습니다.");
      await reload().catch(() => undefined);
    } finally {
      setBusyDepositId(undefined);
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-[#222222]">
              무통장입금 확인
            </h1>
            <p className="mt-1 text-sm text-[#888888]">
              입금을 기다리는 주문을 보고, 들어온 입금을 확인합니다.
            </p>
          </div>
          <button
            onClick={() => void reload()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-2 text-[13px] font-medium text-[#555555] hover:bg-[#F5F5F5]"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            새로고침
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#F24A4A]/20 bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#F24A4A]">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {!loading && !autoConfirm && (
          <div className="mb-5 flex flex-col gap-3 rounded-[12px] border border-[#4285F4]/20 bg-[#EBF3FF] px-5 py-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="text-sm font-bold text-[#4285F4]">
                입금 자동확인을 사용하고 있지 않습니다
              </div>
              <p className="mt-1 text-[13px] leading-5 text-[#4285F4]/85">
                입금은 은행 앱에서 확인한 뒤 <b>결제 확인</b>을 눌러 주세요.
              </p>
            </div>
            <button
              onClick={onStartAuto}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#4285F4] px-4 py-2.5 text-[13px] font-bold text-white hover:bg-[#3367D6]"
            >
              자동확인 시작하기 <ArrowRight size={15} />
            </button>
          </div>
        )}

        {/*
          자동확인을 쓰지 않으면 수집된 입금이 없어 카드 네 칸 중 셋이 0이 된다.
          0만 늘어놓는 것보다 한 줄 요약이 정직하다.
        */}
        {autoConfirm ? (
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <SummaryCard
              icon={<CheckCircle2 size={18} />}
              label="오늘 자동 확인"
              value={`${summary?.autoMatchedCount ?? 0}건`}
            />
            <SummaryCard
              icon={<Clock size={18} />}
              label="입금을 기다리는 주문"
              value={`${summary?.unpaidOrderCount ?? 0}건`}
              highlight={(summary?.unpaidOrderCount ?? 0) > 0}
            />
            <SummaryCard
              icon={<Wallet size={18} />}
              label="오늘 확인한 입금액"
              value={money(summary?.depositTotal ?? 0)}
            />
          </div>
        ) : (
          <div className="mb-5 flex items-center gap-3 rounded-[12px] border border-[#E0E0E0] bg-white px-5 py-4">
            <Clock size={18} className="text-[#BBBBBB]" />
            <b className="text-[15px] font-bold text-[#222222]">
              입금을 기다리는 주문 {unpaid.length}건
            </b>
            {unpaid.length > 0 && (
              <span className="ml-auto text-xs font-medium text-[#888888]">
                가장 오래된 주문은 {elapsedDays(unpaid[0]?.orderedAt)}일째입니다
              </span>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-[12px] border border-[#E0E0E0] bg-white">
          <div className="flex items-center gap-2 border-b border-[#EEEEEE] px-5 py-3.5">
            <h3 className="text-[15px] font-bold text-[#222222]">
              입금을 기다리는 주문
            </h3>
            {unpaid.length > 0 && <Count value={unpaid.length} warn />}
          </div>

          {
            <div>
              {loading ? (
                <Placeholder>주문을 불러오는 중입니다</Placeholder>
              ) : unpaid.length === 0 ? (
                <Placeholder icon={<CheckCircle2 size={26} />}>
                  입금을 기다리는 주문이 없습니다.
                </Placeholder>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left">
                    <thead className="bg-[#FAFAFA] text-xs font-medium text-[#888888]">
                      <tr>
                        <Th>주문 번호</Th>
                        <Th>주문일</Th>
                        <Th>주문자</Th>
                        <Th>주문 금액</Th>
                        <Th>기다린 기간</Th>
                        <Th className="text-right">처리</Th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {unpaid.map((item) => {
                        const waited = elapsedDays(item.orderedAt);
                        return (
                          <tr
                            key={item.orderId}
                            className="border-t border-[#EEEEEE]"
                          >
                            <Td>
                              <a
                                href={`/company/${companyId}/orders?orderId=${item.orderId}`}
                                className="font-medium text-[#4285F4]"
                              >
                                {item.orderNumber ?? `#${item.orderId}`}
                              </a>
                            </Td>
                            <Td className="text-[#888888]">
                              {dateTime(item.orderedAt)}
                            </Td>
                            <Td>
                              {/*
                                주문자는 쇼핑몰 주문이라 항상 있고, 입금자명은 고객이 따로
                                적었을 때만 있다. 그래서 열 이름은 항상 참인 "주문자"로 두고,
                                다른 이름으로 보내겠다고 한 경우에만 그 값과 근거를 함께 보여준다.
                                자동매칭이 대조하는 건 입금자명뿐이다.
                              */}
                              <span className="font-medium text-[#222222]">
                                {item.depositorName ??
                                  item.purchaserName ??
                                  "—"}
                              </span>
                              {item.depositorName && (
                                <span className="ml-1.5 rounded bg-[#E8EFFF] px-1.5 py-0.5 text-[11px] font-medium text-[#4285F4]">
                                  입금자명
                                </span>
                              )}
                            </Td>
                            <Td className="font-medium text-[#222222]">
                              {money(item.orderTotalAmount)}
                            </Td>
                            <Td
                              className={
                                waited >= 2
                                  ? "font-bold text-[#F24A4A]"
                                  : "text-[#888888]"
                              }
                            >
                              {waited === 0 ? "오늘" : `${waited}일`}
                            </Td>
                            <Td className="text-right">
                              {autoConfirm ? (
                                <button
                                  onClick={() => setPicking(item)}
                                  className="inline-flex items-center gap-1 rounded-lg bg-[#222222] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-[#333333]"
                                >
                                  <CheckCircle2 size={14} /> 입금 확인하기
                                </button>
                              ) : (
                                <button
                                  onClick={() => setConfirming(item)}
                                  disabled={busyOrderId === item.orderId}
                                  className="inline-flex items-center gap-1 rounded-lg bg-[#222222] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-[#333333] disabled:opacity-40"
                                >
                                  <CheckCircle2 size={14} /> 결제 확인
                                </button>
                              )}
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <Note>
                {autoConfirm ? (
                  <>
                    자동으로 확인된 주문은 이 목록에서 빠집니다. 남은 주문은{" "}
                    <b>입금 확인하기</b>를 눌러 들어온 입금 중에서 골라 주세요.
                    입금자명이 비어 있는 주문은 자동으로 확인되지 않습니다.
                  </>
                ) : (
                  <>
                    은행에서 입금을 확인하신 뒤 <b>결제 확인</b>을 눌러 주세요.
                  </>
                )}
              </Note>
            </div>
          }
        </div>

        {autoConfirm && (
          <div className="mt-5 overflow-hidden rounded-[12px] border border-[#E0E0E0] bg-white">
            <div className="flex flex-wrap items-center gap-2 border-b border-[#EEEEEE] px-5 py-3.5">
              <h3 className="text-[15px] font-bold text-[#222222]">
                확인된 입금
              </h3>
              {deposits.length > 0 && <Count value={deposits.length} />}
              <div className="ml-auto flex items-center gap-1">
                {RANGES.map((range) => (
                  <button
                    key={range.days}
                    onClick={() => setDays(range.days)}
                    className={`rounded-md px-2.5 py-1 text-[12px] font-medium ${
                      days === range.days
                        ? "bg-[#E8EFFF] text-[#4285F4]"
                        : "text-[#888888] hover:bg-[#F5F5F5]"
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <Placeholder>입금 내역을 불러오는 중입니다</Placeholder>
            ) : deposits.length === 0 ? (
              <Placeholder icon={<Inbox size={26} />}>
                {rangeLabel} 확인한 입금이 없습니다.
              </Placeholder>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-left">
                  <thead className="bg-[#FAFAFA] text-xs font-medium text-[#888888]">
                    <tr>
                      <Th>입금 시각</Th>
                      <Th>입금자명</Th>
                      <Th>입금액</Th>
                      <Th>확인한 주문</Th>
                      <Th className="text-right">처리</Th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {deposits.map((item) => (
                      <tr key={item.id} className="border-t border-[#EEEEEE]">
                        <Td className="text-[#888888]">
                          {dateTime(item.transDt)}
                        </Td>
                        <Td className="font-medium text-[#222222]">
                          {item.depositorName || (
                            <span className="text-[#BBBBBB]">이름 없음</span>
                          )}
                        </Td>
                        <Td className="font-medium text-[#222222]">
                          {money(item.depositAmount)}
                        </Td>
                        <Td>
                          <a
                            href={`/company/${companyId}/orders?orderId=${item.matchedOrderId}`}
                            className="text-[13px] font-medium text-[#4285F4]"
                          >
                            주문 #{item.matchedOrderId}
                          </a>
                        </Td>
                        <Td className="text-right">
                          {item.matchedOrderId && (
                            <button
                              onClick={() => void unlink(item)}
                              disabled={busyDepositId === item.id}
                              className="inline-flex items-center gap-1 rounded-lg border border-[#E0E0E0] px-2.5 py-1.5 text-[12px] font-medium text-[#555555] hover:bg-[#F5F5F5] disabled:opacity-40"
                            >
                              <RotateCcw size={13} /> 확인 취소
                            </button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Note>
              주문까지 확인된 입금만 보입니다. 아직 확인하지 않은 입금은{" "}
              <b>입금 확인하기</b>를 누르셨을 때 보여 드립니다. 잘못 고르셨으면{" "}
              <b>확인 취소</b>로 되돌릴 수 있습니다.
            </Note>
          </div>
        )}
      </div>

      {confirming && (
        <ManualConfirm
          order={confirming}
          onClose={() => setConfirming(undefined)}
          onConfirm={() => void confirmManually(confirming)}
        />
      )}

      {picking && (
        <DepositPicker
          companyId={companyId}
          order={picking}
          onClose={() => setPicking(undefined)}
          onDone={async () => {
            setPicking(undefined);
            await reload();
          }}
          onStale={reload}
        />
      )}
    </>
  );
};

/**
 * 자동확인을 쓰지 않을 때의 결제 확인.
 *
 * 고를 입금이 없다 — 우리는 통장을 보지 않는다. 그래서 이 창이 하는 일은
 * **"입금을 받았다"는 사장님의 진술을 받아 주문을 접수로 넘기는 것**뿐이고,
 * 확정 버튼도 그렇게 읽히도록 적었다.
 */
const ManualConfirm = ({
  order,
  onClose,
  onConfirm,
}: {
  order: UnpaidBankTransferOrder;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={(e) => e.target === e.currentTarget && onClose()}
  >
    <div className="w-full max-w-[420px] overflow-hidden rounded-[12px] bg-white">
      <div className="flex items-center gap-3 border-b border-[#EEEEEE] px-6 py-5">
        <div>
          <h2 className="text-base font-bold text-[#222222]">결제 확인</h2>
          <p className="mt-0.5 text-xs text-[#888888]">
            은행 앱에서 입금을 확인하셨나요?
          </p>
        </div>
        <button
          onClick={onClose}
          className="ml-auto rounded-lg bg-[#F5F5F5] p-2 text-[#888888]"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-6 py-5">
        <div className="rounded-[8px] bg-[#FAFAFA] px-4 py-3 text-[13px]">
          <Row
            label="주문 번호"
            value={order.orderNumber ?? `#${order.orderId}`}
          />
          <Row
            label={order.depositorName ? "입금자명" : "주문자"}
            value={order.depositorName ?? order.purchaserName ?? "—"}
          />
          <Row label="주문 금액" value={money(order.orderTotalAmount)} />
        </div>
        <div className="mt-4 rounded-[8px] bg-[#FAFAFA] px-4 py-3 text-[12px] leading-5 text-[#888888]">
          이 창은 통장을 조회하지 않습니다. 누르시면 이 주문이 <b>바로 접수</b>
          됩니다. 입금이 실제로 들어왔는지 은행 앱에서 먼저 확인해 주세요.{" "}
          <b>되돌릴 수 없습니다.</b>
        </div>
      </div>

      <div className="flex gap-2 border-t border-[#EEEEEE] px-6 py-4">
        <button
          onClick={onClose}
          className="flex-1 rounded-lg border border-[#222222] py-2.5 text-sm font-medium text-[#222222] hover:bg-[#F5F5F5]"
        >
          취소
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 rounded-lg bg-[#222222] py-2.5 text-sm font-medium text-white hover:bg-[#333333]"
        >
          입금 받았습니다 · 접수
        </button>
      </div>
    </div>
  </div>
);

/**
 * 주문 하나에 붙일 입금을 고른다.
 *
 * 후보를 금액으로 거르지 않는다 — 129,000원 주문에 130,000원을 보내는 고객이 있고,
 * 그 건을 빼면 사장님이 찾을 방법이 없다. 다만 **모자란 입금은 고를 수 없다.**
 * 서버가 거부하는 조건이라 화면에서 미리 막는다.
 */
const DepositPicker = ({
  companyId,
  order,
  onClose,
  onDone,
  onStale,
}: {
  companyId: number;
  order: UnpaidBankTransferOrder;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  /** 확인에 실패했을 때 뒤 목록도 다시 받는다. 창을 닫지는 않는다 */
  onStale: () => Promise<void>;
}) => {
  const [candidates, setCandidates] = useState<BankTransferDepositCandidate[]>(
    [],
  );
  const [chosen, setChosen] = useState<number[]>([]);
  const [allowShortfall, setAllowShortfall] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadCandidates = useCallback(async () => {
    const next = await bankTransferAutoConfirmApiService.depositCandidates(
      companyId,
      order.orderId,
    );
    setCandidates(next);
    // 사라진 입금은 고른 채로 두지 않는다. 그대로 두면 같은 실패를 다시 부른다.
    setChosen((current) =>
      current.filter((id) => next.some((item) => item.id === id)),
    );
  }, [companyId, order.orderId]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await loadCandidates();
      } catch (e) {
        if (alive)
          setError(
            e instanceof Error ? e.message : "입금 내역을 불러오지 못했습니다.",
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadCandidates]);

  const picked = candidates.filter((item) => chosen.includes(item.id));
  const total = picked.reduce((sum, item) => sum + item.depositAmount, 0);
  const gap = total - order.orderTotalAmount;
  const short = chosen.length > 0 && gap < 0;
  // 모자라면 확인란을 체크해야만 확정할 수 있다. 서버도 같은 조건으로 막는다.
  const canSubmit = chosen.length > 0 && (gap >= 0 || allowShortfall);

  const toggle = (id: number) =>
    setChosen((current) =>
      current.includes(id)
        ? current.filter((it) => it !== id)
        : [...current, id],
    );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await bankTransferAutoConfirmApiService.matchDeposits(companyId, {
        orderId: order.orderId,
        transactionIds: chosen,
        allowShortfall,
      });
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "입금 확인에 실패했습니다.");
      // 다른 창이나 자동매칭이 먼저 손을 댔을 수 있다. 그때 화면을 그대로 두면
      // 사장님은 이미 없어진 입금을 계속 고르며 같은 실패를 반복한다.
      await loadCandidates().catch(() => undefined);
      await onStale();
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[92vh] w-full max-w-[620px] overflow-auto rounded-[12px] bg-white">
        <div className="flex items-center gap-3 border-b border-[#EEEEEE] px-6 py-5">
          <div>
            <h2 className="text-base font-bold text-[#222222]">입금 확인</h2>
            <p className="mt-0.5 text-xs text-[#888888]">
              이 주문에 해당하는 입금을 골라 주세요. 여러 건을 함께 고를 수
              있습니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg bg-[#F5F5F5] p-2 text-[#888888]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="mb-4 rounded-[8px] bg-[#FAFAFA] px-4 py-3 text-[13px]">
            <Row
              label="주문 번호"
              value={order.orderNumber ?? `#${order.orderId}`}
            />
            <Row
              label={order.depositorName ? "입금자명" : "주문자"}
              value={order.depositorName ?? order.purchaserName ?? "—"}
            />
            <Row label="주문 금액" value={money(order.orderTotalAmount)} />
          </div>

          <div className="mb-2 flex items-center text-[11px] text-[#888888]">
            최근 14일 입금 중{" "}
            <b className="mx-1 text-[#555555]">아직 확인하지 않은 것</b>
            <span className="ml-auto text-[#BBBBBB]">금액이 가까운 순</span>
          </div>

          {error && (
            <div className="mb-3 rounded-lg border border-[#F24A4A]/20 bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#F24A4A]">
              {error}
            </div>
          )}

          {loading ? (
            <Placeholder>입금 내역을 불러오는 중입니다</Placeholder>
          ) : candidates.length === 0 ? (
            <Placeholder icon={<Inbox size={24} />}>
              고를 수 있는 입금이 없습니다.
            </Placeholder>
          ) : (
            <div className="flex max-h-[300px] flex-col gap-2 overflow-auto p-0.5">
              {candidates.map((item) => {
                const selected = chosen.includes(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => toggle(item.id)}
                    className={`flex items-center gap-3 rounded-[8px] border px-4 py-3 text-left ${
                      selected
                        ? "border-[#4285F4] bg-[#E8EFFF]"
                        : "border-[#E0E0E0] hover:border-[#4285F4]/50 hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? "border-[#4285F4] bg-[#4285F4] text-white"
                          : "border-[#BBBBBB]"
                      }`}
                    >
                      {selected && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <b className="block text-sm font-bold text-[#222222]">
                        {money(item.depositAmount)}
                      </b>
                      <span className="mt-0.5 block truncate text-[11px] text-[#888888]">
                        {item.depositorName || "이름 없음"} ·{" "}
                        {dateTime(item.transDt)}
                      </span>
                    </span>
                    <DifferenceBadge value={item.difference} />
                  </button>
                );
              })}
            </div>
          )}

          {/*
            여러 건을 고를 수 있으니 합계를 실시간으로 보여준다.
            모자란지 남는지는 이 한 줄에서 판가름난다.
          */}
          {chosen.length > 0 && (
            <div
              className={`mt-4 rounded-[8px] border px-4 py-3 ${
                gap >= 0
                  ? "border-[#4285F4]/20 bg-[#EBF3FF]"
                  : "border-[#B26A00]/20 bg-[#FFF4E5]"
              }`}
            >
              <div className="flex items-center justify-between text-[13px]">
                <span
                  className={gap >= 0 ? "text-[#4285F4]" : "text-[#B26A00]"}
                >
                  선택한 입금 {chosen.length}건 합계
                </span>
                <b className={gap >= 0 ? "text-[#4285F4]" : "text-[#B26A00]"}>
                  {money(total)}
                </b>
              </div>
              <div className="mt-1.5 text-[12px] leading-5">
                {gap === 0 && (
                  <span className="text-[#4285F4]">
                    주문 금액과 일치합니다.
                  </span>
                )}
                {gap > 0 && (
                  /* TODO 포인트 적립 기능이 생기면 여기서 적립 안내를 덧붙인다 */
                  <span className="text-[#4285F4]">
                    {money(gap)} 더 들어왔습니다.
                  </span>
                )}
                {gap < 0 && (
                  <span className="text-[#B26A00]">
                    {money(-gap)} 모자랍니다. 추가 입금을 기다리거나, 이
                    금액으로 확정하려면 아래를 체크해 주세요.
                  </span>
                )}
              </div>
              {short && (
                <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-[12px] font-medium text-[#B26A00]">
                  <input
                    type="checkbox"
                    checked={allowShortfall}
                    onChange={(e) => setAllowShortfall(e.target.checked)}
                    className="h-3.5 w-3.5 accent-[#B26A00]"
                  />
                  {money(-gap)}은 받지 않고 결제 확인합니다
                </label>
              )}
            </div>
          )}

          <div className="mt-4 rounded-[8px] bg-[#FAFAFA] px-4 py-3 text-[12px] leading-5 text-[#888888]">
            개인 계좌라면 이자나 개인 송금도 함께 보입니다.
          </div>
        </div>

        <div className="flex gap-2 border-t border-[#EEEEEE] px-6 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#222222] py-2.5 text-sm font-medium text-[#222222] hover:bg-[#F5F5F5]"
          >
            취소
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit || submitting}
            className="flex-1 rounded-lg bg-[#222222] py-2.5 text-sm font-medium text-white hover:bg-[#333333] disabled:opacity-40"
          >
            {chosen.length > 1
              ? `${chosen.length}건으로 결제 확인`
              : "선택한 입금으로 결제 확인"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DifferenceBadge = ({ value }: { value: number }) => {
  if (value === 0) {
    return (
      <span className="shrink-0 rounded px-2.5 py-1.5 text-center text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
        금액 일치
      </span>
    );
  }
  const over = value > 0;
  return (
    <span
      className={`shrink-0 rounded px-2.5 py-1.5 text-center text-[11px] font-medium leading-4 ${
        over ? "bg-[#EBF3FF] text-[#4285F4]" : "bg-[#F5F5F5] text-[#888888]"
      }`}
    >
      {over ? "+" : ""}
      {value.toLocaleString("ko-KR")}원
      <br />
      {over ? "더 보냄" : "모자람"}
    </span>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-[#888888]">{label}</span>
    <b className="font-medium text-[#222222]">{value}</b>
  </div>
);

const SummaryCard = ({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <div
    className={`rounded-[12px] border bg-white p-4 ${
      highlight ? "border-[#4285F4]" : "border-[#E0E0E0]"
    }`}
  >
    <div className="flex items-center gap-2 text-xs font-medium text-[#888888]">
      <span className={highlight ? "text-[#4285F4]" : "text-[#BBBBBB]"}>
        {icon}
      </span>
      {label}
    </div>
    <div className="mt-2 text-[20px] font-bold text-[#222222]">{value}</div>
  </div>
);

const Count = ({ value, warn = false }: { value: number; warn?: boolean }) => (
  <span
    className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
      warn ? "bg-[#FFF4E5] text-[#B26A00]" : "bg-[#E8EFFF] text-[#4285F4]"
    }`}
  >
    {value}
  </span>
);

const Th = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => <th className={`px-5 py-3 font-medium ${className}`}>{children}</th>;

const Td = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => <td className={`px-5 py-3.5 ${className}`}>{children}</td>;

const Placeholder = ({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-[13px] text-[#888888]">
    {icon && <span className="text-[#BBBBBB]">{icon}</span>}
    {children}
  </div>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="border-t border-[#EEEEEE] bg-[#FAFAFA] px-5 py-3.5 text-[13px] leading-5 text-[#888888]">
    {children}
  </div>
);
